import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
	createRoutesFromChildren,
	matchRoutes,
	Routes,
	useLocation,
	useNavigationType,
} from 'react-router-dom';
import { ENVIRONMENT_CATEGORY } from '@dealstack/shared/lib/common/environment';
import type { ENVIRONMENT_IDENTIFIER } from '@dealstack/frontend/shared/environment';
import { applyApiErrorMetadata } from '@dealstack/frontend/shared/sentry.utils';
import {
	REPLAY_BLOCK_SELECTORS,
	REPLAY_MASK_SELECTORS,
} from '@dealstack/frontend/shared/sentryPrivacy';

export const initializeSentry = ({
	dsn,
	apiUrl,
	release,
	environment,
}: {
	dsn: string;
	apiUrl: string;
	release: string;
	environment: ENVIRONMENT_IDENTIFIER;
}): void => {
	// `replayIntegration()` builds its Replay eagerly and the constructor throws on a
	// second instance, so a repeat init (HMR re-evaluation) has to be a no-op.
	if (Sentry.getClient()) {
		return;
	}

	const [category] = environment;

	Sentry.init({
		dsn,
		release,
		environment: environment.toString(),
		enabled: category !== ENVIRONMENT_CATEGORY.LOCAL && dsn !== '',
		// Relayed through our API so adblockers that block Sentry's ingest host do not
		// silently drop these users' errors and replays (SentryTunnelController).
		tunnel: `${apiUrl}/s`,
		// Adds request headers and IP for users, for more info visit:
		// https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
		sendDefaultPii: true,

		integrations: [
			Sentry.reactRouterV7BrowserTracingIntegration({
				useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),
			Sentry.httpClientIntegration(),
			Sentry.replayIntegration({
				// Support debugs from replays, so the page stays readable. Sensitive content
				// (2FA secrets, bank identifiers) is masked by selector; see sentryPrivacy.ts.
				maskAllText: false,
				maskAllInputs: false,
				blockAllMedia: false,
				mask: REPLAY_MASK_SELECTORS,
				block: REPLAY_BLOCK_SELECTORS,
				// The default drops sessions shorter than 5s; every production session is kept.
				minReplayDuration: 0,
			}),
		],

		// Applied centrally rather than at the ~23 captureException call sites, so it also
		// covers unhandled rejections and anything added later. An explicit fingerprint
		// set at the call site (reportTransportError) is left alone.
		beforeSend: (event, hint) => applyApiErrorMetadata(event, hint?.originalException),

		// A sample of production is enough for performance data; errors are reported everywhere.
		// https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
		tracesSampleRate: category === ENVIRONMENT_CATEGORY.PRODUCTION ? 0.1 : 0,

		// Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
		tracePropagationTargets: [/^\//, /^https:\/\/dealstack\.io\/api/],

		// Every production session (~22K/month, budgeted as reserved replays); elsewhere only
		// sessions that hit an error.
		// https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
		replaysSessionSampleRate: category === ENVIRONMENT_CATEGORY.PRODUCTION ? 1 : 0,
		replaysOnErrorSampleRate: 1,
	});
};

export const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);
